/* eslint-disable react/sort-comp */
import React from "react";
import PropTypes from "prop-types";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import { capitalize } from "lodash";
import * as notifications from "../notifications/notifications";
import LoadingOverlay from "./LoadingOverlay";
import UserClassificationForm from "./UserClassificationForm";
import ModalWrapper from "./ModalWrapper";
import api from "../services/api";

export default class UserClassification extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      cloud: {
        initial: {
          threat_status: null,
          factor: null,
          threat_name: null,
          isUserClassified: null
        },
        values: {
          threat_status: null,
          factor: null,
          threat_name: null
        },
        loading: true
      },
      local: {
        initial: {
          threat_status: null,
          factor: null,
          threat_name: null,
          isUserClassified: null
        },
        values: {
          threat_status: null,
          factor: null,
          threat_name: null
        },
        loading: true
      },
      errors: {
        cloud: {},
        local: {}
      },
      key: "cloud"
    };
    this.defaultValues = {
      threat_status: "goodware",
      factor: 0,
      threat_name: "Win32.Malware.Generic"
    };
    this.cloudEnabled = window.tcbase.consts.cloud_enabled;
  }

  componentDidMount() {
    if (this.cloudEnabled) {
      this.fetchUserClassificationData("cloud");
    } else {
      this.setState({ key: "local" });
    }
    this.fetchUserClassificationData("local");
  }

  fetchUserClassificationData = type => {
    if (type === "cloud") {
      api
        .getUserCloudClassification({ hash_value: this.props.sampleHash })
        .then(response => {
          this.populateUserClassificationValues(response.data, "cloud");
        });
    } else {
      api
        .getUserClassification({ hash_value: this.props.sampleHash })
        .then(response => {
          this.populateUserClassificationValues(response.data, "local");
        });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    const currentViewData = this.state[this.state.key];
    const factor = currentViewData.values && currentViewData.values.factor;
    const newClassification =
      currentViewData.values && currentViewData.values.threat_status;
    const oldClassification =
      prevState[this.state.key].values &&
      prevState[this.state.key].values.threat_status;
    if (oldClassification && oldClassification !== newClassification) {
      this.adjustMinMaxSliderValues(newClassification, factor);
    }
  }

  populateUserClassificationValues = (apiResponseData, overrideType) => {
    const { threat_status, factor, threat_name, is_user_override } =
      apiResponseData;

    const isUnknownSample = threat_status === "unknown";
    const overrideTypeData = this.state[overrideType];

    overrideTypeData.initial.threat_status = threat_status;
    overrideTypeData.initial.factor = factor;
    overrideTypeData.initial.threat_name = threat_name;
    overrideTypeData.initial.isUserClassified =
      overrideType === "cloud" ? is_user_override : threat_status !== null;

    if (overrideType === "local" && !threat_status) {
      // if there is no local user classification, the userclassification API for local override returns null values
      // so we need to populate them with current classification values from sample model
      this.populateLocalDataWithCurrentValues();
    } else {
      overrideTypeData.values.threat_status = threat_status;
      overrideTypeData.values.factor = isUnknownSample ? 0 : factor;
      overrideTypeData.values.threat_name =
        threat_name || this.defaultValues.threat_name;
      overrideTypeData.loading = false;

      this.setState({
        [overrideType]: overrideTypeData
      });
    }
  };

  populateLocalDataWithCurrentValues = () => {
    const localOverrideState = this.state.local;

    if (!this.props.classification) {
      api.expandedDetailsLocal(this.props.sampleHash).then(response => {
        const { sample } = response.data;
        localOverrideState.values.threat_status = sample.threat_status;
        localOverrideState.values.factor =
          sample.threat_status === "unknown" ? 0 : sample.factor;
        localOverrideState.values.threat_name =
          sample.threat_name || this.defaultValues.threat_name;
        localOverrideState.loading = false;

        this.setState({
          local: localOverrideState
        });
      });
    } else {
      localOverrideState.values.threat_status = this.props.classification;
      localOverrideState.values.factor = this.props.factor || 0;
      localOverrideState.values.threat_name =
        this.props.threatName || this.defaultValues.threat_name;
      localOverrideState.loading = false;

      this.setState({
        local: localOverrideState
      });
    }
  };

  onSuccess = (refreshPage = this.props.refreshPage) => {
    const currentView = this.state.key;
    const viewDisplay = capitalize(currentView);
    const cloudWarning =
      " Once set, please wait for changed classification to propagate from the Cloud locally.";
    notifications.show({
      level: "success clear",
      sticky: false,
      html: false,
      message: `${viewDisplay} user classification changed successfully!${
        currentView === "cloud" ? cloudWarning : ""
      }`
    });
    const { errors } = this.state;
    errors[currentView] = {};
    this.setState({ errors });
    if (refreshPage === true) {
      setTimeout(() => {
        window.location.reload(true);
      }, 5000);
    }
    if (this.props.refreshKendoGrid) {
      this.props.onSuccess(
        this.state[currentView].initial.isUserClassified,
        this.state[currentView].initial
      );
    } else {
      this.props.onSuccess();
    }
  };

  onError = () => {
    notifications.show({
      level: "error clear",
      sticky: true,
      html: false,
      message: "Oops! An error occurred while changing user classification."
    });
    if (this.props.onError) {
      this.props.onError(this.props.sampleHash);
    }
  };

  adjustMinMaxSliderValues = (classification, factor) => {
    const currentView = this.state.key;
    const currentViewData = { ...this.state[currentView] };
    let factorAdjusted = factor;
    if (
      (classification === "goodware" || classification === "unknown") &&
      factor > 5
    ) {
      factorAdjusted = 0;
    } else if (classification === "suspicious" && factor !== 6) {
      factorAdjusted = 6;
    } else if (classification === "malicious" && factor < 7) {
      factorAdjusted = 10;
    }
    currentViewData.values.factor = factorAdjusted;
    this.setState({ [currentView]: currentViewData });
  };

  static discussionComments = {
    save: data => {
      let comment = `<em>User classification changed. Classification set to <strong>${data.threat_status.toUpperCase()}</strong>, Risk Score set to <strong>${
        data.factor
      }</strong>`;
      if (data.threat_status !== "goodware") {
        comment += `, threat name set to <strong>${data.threat_name}</strong>`;
      }
      comment += ". (auto-generated message)</em>";
      return comment;
    },
    delete: data => {
      let comment = `<em>Removed user classification <strong>${data.threat_status.toUpperCase()}</strong>, Risk Factor <strong>${
        data.factor
      }</strong>`;
      if (data.threat_status !== "goodware") {
        comment += ` with threat name <strong>${data.threat_name}</strong>`;
      }
      comment += ". (auto-generated message)</em>";
      return comment;
    }
  };

  addComment = comment => {
    api.writeComment({ hash_value: this.props.sampleHash, comment });
  };

  handleSubmit = () => {
    const values = {
      ...this.state[this.state.key].values
    };
    if (
      values.threat_status === "unknown" ||
      values.threat_status === "goodware"
    ) {
      values.threat_name = "";
    }

    const currentView = this.state.key;

    if (currentView === "cloud") {
      api
        .setUserCloudClassification({
          hash_value: this.props.sampleHash,
          data: values
        })
        .then(() => {
          this.onSuccess(false);
          this.props.onClose();
        })
        .catch(error => {
          if (error.status === 400 && error.data && error.data.detail.fields) {
            const errors = { ...this.state.errors };
            errors[currentView] = error.data.detail.fields;
            if (
              errors[currentView].threat_status &&
              errors[currentView].threat_status !== null
            ) {
              errors[currentView].threat_status[0] =
                "Please select a Classification";
            }
            this.setState({ errors });
          } else {
            console.log(error);
            this.onError();
            this.props.onClose();
          }
        });
    } else {
      api
        .setUserClassification({
          hash_value: this.props.sampleHash,
          data: values
        })
        .then(response => {
          if (this.props.shouldAddCommentOnAction) {
            const comment = this.constructor.discussionComments.save(
              response.data
            );
            this.addComment(comment);
          }
          this.onSuccess();
          this.props.onClose();
        })
        .catch(error => {
          if (error.status === 400 && error.data && error.data.detail.fields) {
            const errors = { ...this.state.errors };
            errors[currentView] = error.data.detail.fields;
            if (
              errors[currentView].threat_status &&
              errors[currentView].threat_status !== null
            ) {
              errors[currentView].threat_status[0] =
                "Please select a Classification";
            }
            this.setState({ errors });
          } else {
            console.log(error);
            this.onError();
            this.props.onClose();
          }
        });
    }
  };

  handleRemove = () => {
    if (this.state.key === "cloud") {
      api
        .deleteUserCloudClassification({ hash_value: this.props.sampleHash })
        .then(() => {
          this.onSuccess(false);
          this.props.onClose();
        })
        .catch(() => {
          this.onError();
          this.props.onClose();
        });
    } else {
      api
        .deleteUserClassification({ hash_value: this.props.sampleHash })
        .then(() => {
          if (this.props.shouldAddCommentOnAction) {
            const comment = this.constructor.discussionComments.delete(
              this.state.local.values
            );
            this.addComment(comment);
          }
          this.onSuccess();
          this.props.onClose();
        })
        .catch(() => {
          this.onError();
          this.props.onClose();
        });
    }
  };

  handleValues = newValues => {
    const currentView = this.state.key;
    const currentViewData = { ...this.state[currentView] };
    currentViewData.values = newValues;
    this.setState({ [currentView]: currentViewData });
  };

  handleInput = event => {
    const target = event.target;
    const errors = { ...this.state.errors };
    errors[this.state.key] = Object.assign(
      {},
      this.state.errors[this.state.key]
    );
    delete errors[this.state.key][target.name];
    this.setState({ errors });
  };

  handleSelect = key => {
    this.setState({ key });
  };

  render() {
    const { onClose } = this.props;
    const { cloud, local, errors } = this.state;

    const localUserClassificationForm = local.values && (
      <UserClassificationForm
        initial={local.values}
        values={local.values}
        errors={errors.local}
        onValues={this.handleValues}
        onChange={this.handleInput}
        onCancel={onClose}
        isUserClassified={local.initial.isUserClassified}
        onRemove={this.handleRemove}
        onSubmit={this.handleSubmit}
        userClassificationType="local"
        key={local.initial.isUserClassified}
      />
    );

    const loadingBlock = (
      <div className="user-classification-popup__loading-container">
        <LoadingOverlay />
        <img
          className="user-classification-popup__placeholder-image"
          src={`${window.tcbase.urls.static}images/user-classification-placeholder.png`}
        />
      </div>
    );

    const renderTabTitle = (title, testid) => (
      <span data-testid={testid}>{title}</span>
    );

    return (
      <ModalWrapper
        show
        title="Set Classification"
        onHide={onClose}
        size="lg"
        className={`user-classification-popup modal--borderless ${
          this.cloudEnabled && "user-classification-popup--tabs"
        }`}
        data-testid="user_classification_modal"
      >
        {this.cloudEnabled ? (
          <Tabs
            id="user-classification-popup-tabs"
            variant="tabs"
            activeKey={this.state.key}
            onSelect={this.handleSelect}
            transition
            data-testid="user_classification_tabs_container"
          >
            <Tab
              key={"cloud"}
              eventKey={"cloud"}
              title={renderTabTitle(
                "Spectra Intelligence",
                "user_classification_tab_nav_item_cloud"
              )}
              data-testid="user_classification_tab_content_cloud"
            >
              {cloud.loading
                ? loadingBlock
                : cloud.values && (
                    <UserClassificationForm
                      initial={cloud.values}
                      values={cloud.values}
                      errors={errors.cloud}
                      onValues={this.handleValues}
                      onChange={this.handleInput}
                      onCancel={onClose}
                      isUserClassified={cloud.initial.isUserClassified}
                      onRemove={this.handleRemove}
                      onSubmit={this.handleSubmit}
                      userClassificationType="cloud"
                      key={cloud.initial.isUserClassified}
                    />
                  )}
            </Tab>
            <Tab
              key={"local"}
              eventKey={"local"}
              title={renderTabTitle(
                "Local",
                "user_classification_tab_nav_item_local"
              )}
              data-testid="user_classification_tab_content_local"
            >
              {local.loading ? loadingBlock : localUserClassificationForm}
            </Tab>
          </Tabs>
        ) : (
          localUserClassificationForm
        )}
      </ModalWrapper>
    );
  }
}

UserClassification.propTypes = {
  sampleHash: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  shouldAddCommentOnAction: PropTypes.bool,
  refreshPage: PropTypes.bool,
  refreshKendoGrid: PropTypes.bool,
  classification: PropTypes.string,
  factor: PropTypes.number,
  threatName: PropTypes.string
};

UserClassification.defaultProps = {
  shouldAddCommentOnAction: true,
  onSuccess: () => {}
};
